import type { User } from "@/types";

import { atomWithStorage } from "jotai/utils";
import { atomWithQuery, atomWithMutation, queryClientAtom } from "jotai-tanstack-query";
import { atom, useAtomValue } from "jotai";
import { toast } from "react-toastify";

import { apiClient } from "@/utils/api";

// Safe storage wrapper that falls back to memory if localStorage is not available
const safeStorage = {
  getItem: (key: string): string | null => {
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  },
  setItem: (key: string, newValue: string | null): void => {
    try {
      if (newValue === null) {
        localStorage.removeItem(key);
      } else {
        localStorage.setItem(key, newValue);
      }
    } catch {
      // Ignore storage errors
    }
  },
  removeItem: (key: string): void => {
    try {
      localStorage.removeItem(key);
    } catch {
      // Ignore storage errors
    }
  },
};

export const tokenAtom = atomWithStorage<string | null>("token", null, safeStorage);
export const isAuthenticatedAtom = atom<boolean>(false);

// Server-synced profile query (internal cache)
export const profileQueryAtom = atomWithQuery((get) => {
  const token = get(tokenAtom);

  return {
    queryKey: ["profile"],
    queryFn: async () => {
      try {
        const { data } = await apiClient.get("/me");

        return data.data;
      } catch (error: any) {
        if (error.response?.status === 401 || error.response?.status >= 500) {
          console.warn("Profile fetch failed:", error.response?.status, error.message);

          return null;
        }
        throw error;
      }
    },
    enabled: Boolean(token),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    retry: 1,
    retryDelay: 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  };
});

/**
 * profileAtom - Unified profile atom for UI consumption.
 *
 * Read:
 *  - Returns cached server data from `profileQueryAtom`.
 *
 * Write (actions):
 *  - { type: "refetch" }            → invalidate ["profile"] and refetch from server
 *
 * Usage examples:
 *  // read (uses cached server data)
 *  // const profile = useAtomValue(profileAtom)
 *
 *  // force refresh from server
 *  // const setProfile = useSetAtom(profileAtom)
 *  // setProfile({ type: "refetch" })
 */
export const profileAtom = atom<User | null, [{ type: "refetch" }], void>(
  (get) => {
    const q = get(profileQueryAtom);

    return (q?.data as User | null) ?? null;
  },
  (get, _set, ...args) => {
    const [action] = args;

    if (action?.type === "refetch") {
      const qc = get(queryClientAtom);

      qc.invalidateQueries({ queryKey: ["profile"] });
    }
  },
);

// Login mutation atom
export const loginAtom = atomWithMutation(() => ({
  mutationKey: ["auth", "login"],
  mutationFn: async (credentials: { email: string; password: string }) => {
    const { data } = await apiClient.post("/login", credentials);

    return data;
  },
  onSuccess: (data: any) => {
    toast.success(data.meta.message);
  },
  onError: (error: any) => {
    // Error is handled by axios interceptor
  },
}));

// Permissions atom - extracts all permissions from user roles
export const userPermissionsAtom = atom<string[]>((get) => {
  const profile = get(profileAtom);

  if (!profile?.roles) {
    return [];
  }

  // Extract all unique permissions from all roles
  const allPermissions = new Set<string>();

  profile.roles.forEach(role => {
    if (role.permissions) {
      role.permissions.forEach(permission => {
        allPermissions.add(permission);
      });
    }
  });

  return Array.from(allPermissions);
});

// Permission check atom - checks if user has specific permission
export const hasPermissionAtom = atom((get) => {
  const permissions = get(userPermissionsAtom);

  return (permission: string): boolean => {
    return permissions.includes(permission);
  };
});

// Convenience hook for permission checking
export const useHasPermission = () => {
  const hasPermission = useAtomValue(hasPermissionAtom);
  return hasPermission;
};
